/**
 * PublicFormFillPage — the anonymous, no-login form-fill surface served at
 * /p/:token (outside ProtectedRoute). It reads the link config + published form
 * definitions from the `public-form` edge function and drives the SAME shared
 * form engine (runFormEngine + FormRenderer) the authenticated app uses, so
 * conditional logic, calculated fields, blocking errors, and soft-warning
 * justifications behave identically. Single instance, no draft, no autosave.
 */
import { useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, AlertTriangle, CheckCircle2, ArrowLeft, ArrowRight, Send, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { supabase } from '@/lib/supabase/client'
import { FormRenderer, runFormEngine } from '@/lib/form-engine'
import type { FormDefinition } from '@/features/form-builder/api/form-builder-api'
import type { UploadedFileRef } from '@/features/records/api/file-upload-api'

interface PublicForm {
  id: string
  version_id: string
  title: string
  instance_type: string
  definition: FormDefinition
}

interface ReadResult {
  status?: 'ok'
  error?: string
  title?: string
  collect_contact?: boolean
  allow_files?: boolean
  forms?: PublicForm[]
}

const ERROR_COPY: Record<string, { title: string; body: string }> = {
  invalid_link: { title: 'لینک نامعتبر است', body: 'این نشانی معتبر نیست یا حذف شده است.' },
  revoked: { title: 'لینک لغو شده است', body: 'این لینک دیگر فعال نیست.' },
  expired: { title: 'لینک منقضی شده است', body: 'مهلت تکمیل این فرم به پایان رسیده است.' },
  capped: { title: 'ظرفیت تکمیل شده', body: 'سقف تعداد ارسال این فرم پر شده است.' },
  disabled: { title: 'غیرفعال', body: 'لینک‌های عمومی برای این پروژه فعال نیست.' },
  project_inactive: { title: 'پروژه غیرفعال است', body: 'این پروژه در حال حاضر فعال نیست.' },
  no_forms: { title: 'فرمی موجود نیست', body: 'هیچ فرم منتشرشده‌ای برای این لینک تعریف نشده است.' },
}

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

async function readLink(token: string): Promise<ReadResult> {
  const { data, error } = await supabase.functions.invoke('public-form', {
    body: { action: 'read', token },
  })
  if (error) return { error: 'invalid_link' }
  return data as ReadResult
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" dir="rtl">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 text-center">{children}</div>
    </div>
  )
}

export function PublicFormFillPage() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading } = useQuery({
    queryKey: ['public-form', token],
    queryFn: () => readLink(token!),
    enabled: !!token,
    retry: false,
  })

  const forms = data?.forms ?? []
  const collectContact = data?.collect_contact ?? false
  const totalSteps = forms.length + (collectContact ? 1 : 0)

  const [step, setStep] = useState(0)
  const [valuesByForm, setValuesByForm] = useState<Record<string, Record<string, unknown>>>({})
  const [justByForm, setJustByForm] = useState<Record<string, Record<string, string>>>({})
  const [attempted, setAttempted] = useState<Set<number>>(new Set())
  const [contact, setContact] = useState({ name: '', email: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // A draft record is created lazily on the first file upload (init phase) and
  // reused by every subsequent signed upload + the final submit.
  const recordIdRef = useRef<string | null>(null)

  const ensureRecordId = async (): Promise<string> => {
    if (recordIdRef.current) return recordIdRef.current
    const { data, error } = await supabase.functions.invoke('public-form', {
      body: { action: 'init', token },
    })
    if (error) throw new Error('خطا در آماده‌سازی فرم')
    const r = data as { status?: string; record_id?: string; error?: string }
    if (r.status !== 'ok' || !r.record_id) {
      throw new Error(ERROR_COPY[r.error ?? '']?.body ?? 'بارگذاری فایل مجاز نیست')
    }
    recordIdRef.current = r.record_id
    return r.record_id
  }

  // Upload strategy for anonymous file fields: request a signed upload URL from
  // the edge function, then PUT the file straight to storage (no auth needed).
  const makeUpload = (formId: string) => async (file: File, fieldKey: string): Promise<UploadedFileRef> => {
    const recordId = await ensureRecordId()
    const { data, error } = await supabase.functions.invoke('public-form', {
      body: { action: 'sign_upload', token, record_id: recordId, form_id: formId, field_key: fieldKey, filename: file.name },
    })
    if (error) throw new Error('خطا در آماده‌سازی بارگذاری')
    const s = data as { status?: string; bucket?: string; path?: string; token?: string }
    if (s.status !== 'ok' || !s.path || !s.token || !s.bucket) throw new Error('بارگذاری مجاز نیست')
    const { error: upErr } = await supabase.storage.from(s.bucket).uploadToSignedUrl(s.path, s.token, file)
    if (upErr) throw new Error('خطا در بارگذاری فایل')
    return {
      storagePath: s.path,
      bucket: s.bucket,
      originalName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    }
  }

  const onContactStep = collectContact && step === forms.length
  const currentForm = !onContactStep ? forms[step] : undefined

  const snapshot = useMemo(() => {
    if (!currentForm) return null
    const vals = valuesByForm[currentForm.id] ?? {}
    return runFormEngine(currentForm.definition.fields, currentForm.definition.conditional_rules ?? [], vals)
  }, [currentForm, valuesByForm])

  if (isLoading) {
    return (
      <CenteredCard>
        <Loader2 className="w-8 h-8 text-primary mx-auto animate-spin" />
        <p className="text-sm text-muted-foreground mt-4">در حال بارگذاری فرم...</p>
      </CenteredCard>
    )
  }

  if (!data || data.error || forms.length === 0) {
    const copy = ERROR_COPY[data?.error ?? 'invalid_link'] ?? ERROR_COPY.invalid_link
    return (
      <CenteredCard>
        <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
        <h1 className="text-base font-semibold text-foreground">{copy.title}</h1>
        <p className="text-sm text-muted-foreground mt-1.5">{copy.body}</p>
      </CenteredCard>
    )
  }

  if (submitted) {
    return (
      <CenteredCard>
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
        <h1 className="text-lg font-semibold text-foreground">ثبت شد</h1>
        <p className="text-sm text-muted-foreground mt-1.5">پاسخ شما با موفقیت ثبت شد. سپاسگزاریم.</p>
      </CenteredCard>
    )
  }

  // ── Per-step validation gating ────────────────────────────────────────────
  const errorCount = snapshot ? Object.keys(snapshot.blockingErrors).length : 0
  const missingJust = snapshot
    ? snapshot.softWarnings.filter(
        (w) => !((justByForm[currentForm!.id] ?? {})[`${w.fieldKey}_${w.ruleId}`] ?? '').trim(),
      )
    : []
  const contactValid = !onContactStep || !contact.email || /\S+@\S+\.\S+/.test(contact.email)
  const stepValid = onContactStep ? contactValid : errorCount === 0 && missingJust.length === 0

  const setField = (formId: string, key: string, v: unknown) =>
    setValuesByForm((prev) => ({ ...prev, [formId]: { ...(prev[formId] ?? {}), [key]: v } }))
  const setJust = (formId: string, jKey: string, v: string) =>
    setJustByForm((prev) => ({ ...prev, [formId]: { ...(prev[formId] ?? {}), [jKey]: v } }))

  const markAttempted = () => setAttempted((prev) => new Set(prev).add(step))

  const goNext = () => {
    markAttempted()
    if (!stepValid) return
    setStep((s) => Math.min(s + 1, totalSteps - 1))
  }
  const goBack = () => setStep((s) => Math.max(s - 1, 0))

  const handleSubmit = async () => {
    markAttempted()
    if (!stepValid) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      // Field values are recomputed/validated server-side regardless.
      const fieldValuesByForm: Record<string, Record<string, unknown>> = {}
      const fileRefsByForm: Record<string, unknown[]> = {}
      for (const f of forms) {
        const vals = valuesByForm[f.id] ?? {}
        fieldValuesByForm[f.id] = vals
        const refs: unknown[] = []
        for (const fld of (f.definition.fields ?? []) as Array<{ field_key: string; field_type: string }>) {
          if (fld.field_type !== 'file' && fld.field_type !== 'image') continue
          const v = vals[fld.field_key]
          if (v && typeof v === 'object' && 'storagePath' in v) {
            const r = v as UploadedFileRef
            refs.push({
              field_key: fld.field_key,
              path: r.storagePath,
              bucket: r.bucket,
              original_name: r.originalName,
              mime_type: r.mimeType,
              size_bytes: r.sizeBytes,
            })
          }
        }
        if (refs.length) fileRefsByForm[f.id] = refs
      }

      const { data: res, error } = await supabase.functions.invoke('public-form', {
        body: {
          action: 'submit',
          token,
          record_id: recordIdRef.current,
          field_values_by_form: fieldValuesByForm,
          justifications_by_form: justByForm,
          file_refs_by_form: fileRefsByForm,
          submitter_name: collectContact ? contact.name || null : null,
          submitter_email: collectContact ? contact.email || null : null,
        },
      })
      if (error) throw new Error('network')
      const r = res as { status?: string; error?: string }
      if (r?.status === 'ok') {
        setSubmitted(true)
        return
      }
      if (r?.error === 'validation_failed' || r?.error === 'justification_required') {
        setSubmitError('برخی پاسخ‌ها معتبر نیستند. لطفاً گام‌ها را بازبینی کنید.')
        return
      }
      if (r?.error === 'not_implemented') {
        setSubmitError('ثبت عمومی هنوز فعال نشده است.')
        return
      }
      setSubmitError(ERROR_COPY[r?.error ?? '']?.body ?? 'ثبت با خطا مواجه شد. دوباره تلاش کنید.')
    } catch {
      setSubmitError('خطای شبکه. اتصال خود را بررسی کنید.')
    } finally {
      setSubmitting(false)
    }
  }

  const isLastStep = step === totalSteps - 1
  const showAttempt = attempted.has(step)

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <div className="max-w-xl mx-auto px-4 py-8">
        {/* Header + progress */}
        <div className="mb-6">
          <h1 className="text-lg font-semibold text-foreground">{data.title}</h1>
          <div className="flex items-center gap-1.5 mt-3">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-primary' : 'bg-muted'}`}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            گام {(step + 1).toLocaleString('fa-IR')} از {totalSteps.toLocaleString('fa-IR')}
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5">
          {onContactStep ? (
            <div className="space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-foreground">اطلاعات تماس (اختیاری)</h2>
                <p className="text-xs text-muted-foreground mt-0.5">در صورت تمایل برای پیگیری، نام و ایمیل خود را وارد کنید.</p>
              </div>
              <div className="space-y-1.5">
                <Label>نام</Label>
                <Input value={contact.name} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>ایمیل</Label>
                <Input
                  type="email"
                  dir="ltr"
                  value={contact.email}
                  onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                />
                {showAttempt && !contactValid && (
                  <p className="text-xs text-destructive">ایمیل معتبر نیست.</p>
                )}
              </div>
            </div>
          ) : (
            currentForm && (
              <>
                <h2 className="text-sm font-semibold text-foreground mb-3">{parseTitle(currentForm.title)}</h2>
                {showAttempt && errorCount > 0 && (
                  <div className="mb-3 flex items-center gap-2 bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2 text-xs text-red-400">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    لطفاً خطاهای مشخص‌شده را رفع کنید.
                  </div>
                )}
                {showAttempt && errorCount === 0 && missingJust.length > 0 && (
                  <div className="mb-3 flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2 text-xs text-yellow-400">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    برای هشدارها توجیه وارد کنید.
                  </div>
                )}
                <FormRenderer
                  definition={currentForm.definition}
                  values={valuesByForm[currentForm.id] ?? {}}
                  onChange={(k, v) => setField(currentForm.id, k, v)}
                  mode="data-entry"
                  submitAttempted={showAttempt}
                  justifications={justByForm[currentForm.id] ?? {}}
                  onJustificationChange={(jKey, val) => setJust(currentForm.id, jKey, val)}
                  uploadCtx={
                    data.allow_files
                      ? { projectId: '', recordId: '', formId: currentForm.id, submissionId: null, upload: makeUpload(currentForm.id) }
                      : undefined
                  }
                />
              </>
            )
          )}
        </div>

        {submitError && (
          <div className="mt-4 flex items-center gap-2 bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2 text-xs text-red-400">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {submitError}
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between mt-5">
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={goBack} disabled={step === 0 || submitting}>
            <ArrowRight className="w-4 h-4" />
            قبلی
          </Button>
          {isLastStep ? (
            <Button size="sm" className="gap-1.5" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              ثبت نهایی
            </Button>
          ) : (
            <Button size="sm" className="gap-1.5" onClick={goNext} disabled={submitting}>
              بعدی
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
